import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { QuizQuestion } from "@/types/quiz";
import { ScoringRuleEditor } from "./ScoringRuleEditor";
import { RenameQuestionKeyDialog } from "./RenameQuestionKeyDialog";
import { aggregateDrafts, type DraftRule, type DraftRuleAggregate } from "./types";

interface QuestionGroupProps {
  question: QuizQuestion;
  questionIndex: number;
  availableTags: string[];
  getDraftsForAnswer: (answerId: string) => DraftRule[];
  isDirtyForAnswer: (answerId: string) => boolean;
  updateAggregate: (
    answerId: string,
    aggregateId: string,
    updated: DraftRuleAggregate,
  ) => void;
  removeAggregate: (answerId: string, aggregateId: string) => void;
  addDraft: (answerId: string) => void;
  quizId?: string;
  allQuestionKeys?: string[];
  affectedRulesCount?: number;
}

export function QuestionGroup({
  question,
  questionIndex,
  availableTags,
  getDraftsForAnswer,
  isDirtyForAnswer,
  updateAggregate,
  removeAggregate,
  addDraft,
  quizId,
  allQuestionKeys,
  affectedRulesCount,
}: QuestionGroupProps) {
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const questionOptions = [...(question.options ?? [])].sort((a, b) =>
    a.text.localeCompare(b.text, "de", { sensitivity: "base" }),
  );
  const hasAnyDirty = questionOptions.some((o) => isDirtyForAnswer(o.id));
  const renameEnabled = quizId !== undefined && allQuestionKeys !== undefined;

  return (
    <AccordionItem
      value={question.questionKey}
      className="rounded-lg border px-4"
    >
      <AccordionTrigger className="py-3 hover:no-underline">
        <span className="flex flex-1 items-center gap-2 text-left font-medium">
          <span className="min-w-0">
            Frage {questionIndex + 1}: &ldquo;{question.questionText}&rdquo;
          </span>
          {hasAnyDirty && (
            <span className="ml-2 shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
              Geändert
            </span>
          )}
          {renameEnabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRenameDialogOpen(true);
              }}
              className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={`Frage-Key "${question.questionKey}" umbenennen`}
              title="Frage-Key umbenennen"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </span>
      </AccordionTrigger>

      <AccordionContent>
        <div className="space-y-4 pt-2">
          {questionOptions.map((option) => {
            const drafts = getDraftsForAnswer(option.id);
            const aggregates = aggregateDrafts(drafts);
            const isDirty = isDirtyForAnswer(option.id);

            return (
              <div
                key={option.id}
                className={`rounded-md border p-3 ${isDirty ? "border-orange-300 bg-orange-50/30" : ""}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Antwort: &ldquo;{option.text}&rdquo;
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {aggregates.length}{" "}
                    {aggregates.length === 1 ? "Regel" : "Regeln"}
                  </span>
                </div>

                {aggregates.length > 0 && (
                  <div className="space-y-2 pl-2">
                    {aggregates.map((agg) => (
                      <ScoringRuleEditor
                        key={agg.aggregateId}
                        aggregate={agg}
                        suggestions={availableTags}
                        onChange={(updated) =>
                          updateAggregate(option.id, agg.aggregateId, updated)
                        }
                        onRemove={() =>
                          removeAggregate(option.id, agg.aggregateId)
                        }
                      />
                    ))}
                  </div>
                )}

                {aggregates.length === 0 && (
                  <p className="py-2 pl-2 text-xs text-muted-foreground">
                    Keine Regeln — klicke &quot;+ Regel&quot; um eine
                    Zuordnung zu erstellen.
                  </p>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs"
                  onClick={() => addDraft(option.id)}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Regel hinzufügen
                </Button>
              </div>
            );
          })}
        </div>
      </AccordionContent>

      {renameEnabled && (
        <RenameQuestionKeyDialog
          open={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          quizId={quizId!}
          oldKey={question.questionKey}
          affectedRulesCount={affectedRulesCount ?? 0}
          existingKeys={new Set(allQuestionKeys)}
        />
      )}
    </AccordionItem>
  );
}
